import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import { useAuth } from '../lib/auth.jsx';
import DashboardPicker from './DashboardPicker.jsx';
import AskOwlMetric from './AskOwlMetric.jsx';
import { visibleFilters, setFilterAt, removeFilterAt } from '../lib/metricFilters.mjs'; // index-safe filter-row edits (pure, unit-tested)

// Set or edit a metric alert (insight → action). Like the goal editor, you point
// it at a single-value (KPI) tile you already look at — the alert watches that live
// number and pings you when it crosses a line. Dual-surface: identical for a client
// self-serving and an admin acting on their behalf; the server guard decides who may
// write. `entityId` scopes the tile catalogue; `suiteId` is the event it belongs to.
//
// Mobile-first: a single stacked column, a template gallery so setup is a tap +
// one number, and a plain-English preview so the rule always reads back clearly.

// Suggested operational areas for the alert tag (free text still allowed). Drives the
// one-row-per-tag grouping on the Alerts page. Mirrors the goal tags.
const ALERT_TAGS = ['Ticketing', 'Cashless', 'Access control', 'Audience', 'Marketing', 'Revenue', 'F&B / Bar', 'Sponsorship', 'Operations'];

// Repeat cadence in plain language, stored as the existing cooldown (minutes) —
// "every 6 hours" is a developer's unit; people think daily / weekly / monthly.
// Anything that isn't a preset reads as custom "every X days".
const CADENCES = { hourly: 60, daily: 1440, weekly: 10080, monthly: 43200 };
const cadenceOf = (min) => Object.keys(CADENCES).find((k) => CADENCES[k] === Number(min)) || 'custom';
const cadenceText = (min) => {
  const k = cadenceOf(min);
  if (k !== 'custom') return { hourly: 'once an hour', daily: 'once a day', weekly: 'once a week', monthly: 'once a month' }[k];
  const d = Math.max(1, Math.round(Number(min) / 1440));
  return `once every ${d} day${d === 1 ? '' : 's'}`;
};

export default function AlertEditor({ entityId, suiteId, suiteName, suiteEvent = '', alert, smsAvailable = false, slackAvailable = false, initialTemplate = null, prefillName = '', onClose, onSaved }) {
  const isMobile = useIsMobile();
  const { isAdmin } = useAuth();
  const editing = !!alert;

  const [name, setName] = useState(alert?.name || prefillName || ''); // prefillName: an Actions-hub draft rides in
  const [ruleType, setRuleType] = useState(alert?.ruleType || 'threshold');
  const [dashboardId, setDashboardId] = useState(alert?.dashboardId || '');
  const [tileId, setTileId] = useState(alert?.tileId || '');
  const [operator, setOperator] = useState(alert?.operator || 'gte');
  const [threshold, setThreshold] = useState(alert ? String(alert.threshold ?? '') : '');
  const [unit, setUnit] = useState(alert?.unit || 'tickets');
  const [tag, setTag] = useState(alert?.tag || ''); // operational area — groups the Alerts list one row per tag
  const [customCats, setCustomCats] = useState([]); // the client's own categories (shared with goals)
  const [channels, setChannels] = useState(alert?.channels || ['push']);
  const [smsRecipients, setSmsRecipients] = useState((alert?.smsRecipients || []).join(', '));
  // Who this alert is for. Empty = the whole client team, which is how alerts
  // have always behaved — so an existing alert opens on "Everyone" and stays so.
  const [recipients, setRecipients] = useState(alert?.recipients || []);
  const [team, setTeam] = useState(null); // [{id,name,email}] | null while loading
  useEffect(() => {
    if (!entityId) return;
    api.myTeamNames(entityId)
      .then((r) => setTeam((r.members || []).map((m) => ({ id: m.id, name: m.name, isYou: m.isYou }))))
      .catch(() => setTeam([]));
  }, [entityId]);
  const [priority, setPriority] = useState(alert?.priority || 'normal');
  const [frequency, setFrequency] = useState(alert?.frequency || 'once');
  const [cooldownMin, setCooldownMin] = useState(alert?.cooldownMin != null ? String(alert.cooldownMin) : '1440'); // default: at most once a day
  const cadenceKey = cadenceOf(cooldownMin);
  const customDays = Math.max(1, Math.round((Number(cooldownMin) || 1440) / 1440));
  const [quietStart, setQuietStart] = useState(alert?.quietStart || '');
  const [quietEnd, setQuietEnd] = useState(alert?.quietEnd || '');

  const [cat, setCat] = useState(null);          // tile catalogue { dashboards: [...] }
  const [preview, setPreview] = useState(null);   // live value of the picked target

  // Custom-metric source (alert on a raw measure + dimension filter, no tile).
  const [source, setSource] = useState(alert?.source || 'tile');   // 'tile' | 'metric'
  const [exCat, setExCat] = useState(null);        // { explores: [...] }
  const [exKey, setExKey] = useState(alert?.source === 'metric' && alert?.model ? `${alert.model}::${alert.view}` : '');
  const [measure, setMeasure] = useState(alert?.measure || '');
  const [mFilters, setMFilters] = useState(() => Object.entries(alert?.metricFilters || {}).map(([field, value]) => ({ field, value })));
  const [filterVals, setFilterVals] = useState({}); // dimensionField -> [values] | 'loading'
  // A template may CURATE the filter dropdown (payload.filterAllow: dimension names/
  // label patterns) so users see Ticket Type + Category, not every LookML dimension.
  const [filterAllow, setFilterAllow] = useState(null);
  // A template may name its metric by LABEL instead of model/view (e.g. the GA4
  // explore, whose LookML names vary) — resolved against the catalogue once loaded.
  const [pendingMetric, setPendingMetric] = useState(null); // { exploreLabel, measureLabel }

  const [templates, setTemplates] = useState([]); // reusable alert templates (this client + global)
  const [tmplBusy, setTmplBusy] = useState(false);
  const [pendingRefs, setPendingRefs] = useState(null); // tile ref from a template, resolved once the catalogue loads

  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [err, setErr] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);

  // Lock the page behind the sheet + close on Escape (mirrors TileAlertModal).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  // Load the client's dashboards/tiles for the tile picker (once, when used).
  useEffect(() => { if (source === 'tile' && !cat && entityId) api.getMyDigestTiles(entityId).then(setCat).catch(() => setCat({ dashboards: [] })); }, [source, cat, entityId]);
  // Load the metric catalogue (explores the client already uses) when building a metric.
  // Keep the failure rather than swallowing it into a bare empty list — an empty
  // "Choose a data source…" with no reason is undebuggable (see GoalEditor).
  useEffect(() => { if (source === 'metric' && !exCat && suiteId) api.alertMetricCatalog(suiteId).then(setExCat).catch((e) => setExCat({ explores: [], error: String(e.message || e).slice(0, 300) })); }, [source, exCat, suiteId]);

  // The chosen explore + its measures/dimensions.
  const explores = exCat?.explores || [];
  const curExplore = explores.find((e) => `${e.model}::${e.view}` === exKey) || null;
  const measuresFor = () => curExplore?.measures || [];
  const allDims = () => curExplore?.dimensions || [];
  // The filter dropdown honours a template's curation; if the allow-list matches
  // nothing on this explore (user switched explores), fall back to everything so
  // the dropdown can never go empty.
  const dimsFor = () => {
    const all = allDims();
    if (!Array.isArray(filterAllow) || !filterAllow.length) return all;
    const match = all.filter((d) => filterAllow.some((a) => d.name === a || new RegExp(a, 'i').test(d.label || '') || new RegExp(a, 'i').test(d.name || '')));
    return match.length ? match : all;
  };
  const measureObj = () => measuresFor().find((m) => m.name === measure) || null;
  const dimLabel = (name) => allDims().find((d) => d.name === name)?.label || name;
  const metricFiltersObj = () => { const o = {}; for (const f of mFilters) if (f.field && f.value) o[f.field] = f.value; return o; };
  // The event-name dimension for this explore — so the alert can be pinned to ONE
  // event and its number can never span others. Prefer the CANONICAL core_events.name;
  // then an explicit "Event"/"Event Name" label — but never a cashless / comparison /
  // past-event variant (e.g. "Cashless Events Name"), which filters the wrong join and
  // reads 0. (Also not "Event Date"/"Event Type".)
  const notEventName = (s) => /cashless|comparison|compare|past|previous|prior|transaction/i.test(String(s || ''));
  const eventDim = allDims().find((d) => d.name === 'core_events.name')
    || allDims().find((d) => /^(current )?event( name)?$/i.test(String(d.label).trim()) && !notEventName(d.label) && !notEventName(d.name))
    || allDims().find((d) => /\bevent\b/i.test(d.label) && /name/i.test(d.label) && !notEventName(d.label) && !notEventName(d.name))
    || allDims().find((d) => /(^|[._])events?[._]name$/i.test(d.name) && !notEventName(d.name))
    || null;
  const eventField = eventDim?.name || '';
  const eventValue = eventField ? (mFilters.find((f) => f.field === eventField)?.value || '') : '';
  const setEventValue = (v) => setMFilters((fs) => { const others = fs.filter((f) => f.field !== eventField); return v ? [{ field: eventField, value: v }, ...others] : others; });
  // Ask the Owl to build the metric (shared with the goal editor) — fills these
  // pickers in from a plain-language description; the person still reviews and saves.
  const fillFromOwl = (m) => {
    setExKey(`${m.model}::${m.view}`);
    setMeasure(m.measure || '');
    setMFilters([...(m.event ? [{ field: m.event.field, value: m.event.value }] : []), ...(m.filters || []).map((f) => ({ field: f.field, value: f.value }))]);
    setFilterVals((v) => { const next = { ...v }; for (const f of (m.filters || [])) if (Array.isArray(f.options) && f.options.length) next[valKey(f.field, m.event?.value || '')] = f.options; return next; });
  };
  // The scope the chosen target is counted under — shown next to "This reads" so the
  // number is never ambiguous. The event leads; explicit metric filters follow.
  const scopeLabel = () => {
    const ev = eventValue || suiteEvent || suiteName || 'this event';
    const fs = mFilters.filter((f) => f.field && f.value && f.field !== eventField).map((f) => `${dimLabel(f.field)} = ${f.value}`);
    return fs.length ? `${ev} · ${fs.join(' · ')}` : ev;
  };
  const metricLabelStr = () => {
    const ml = measureObj()?.label || '';
    const fs = mFilters.filter((f) => f.field && f.value).map((f) => `${dimLabel(f.field)} = ${f.value}`);
    return fs.length ? `${ml} · ${fs.join(', ')}` : ml;
  };
  // A value list belongs to ONE event — this year's Ticket Types are not last year's —
  // so the cache is keyed by the event in force, not by the field alone. Keying by
  // field only meant the first list fetched (often before the event had been pinned,
  // i.e. every event's values) stuck for the whole session and changing the Event
  // never refreshed it. The Event picker itself is client-wide, so it keys on its
  // own name.
  const valKey = (field, ev = eventValue) => (field === eventField ? field : `${field}@@${ev || '*'}`);
  const valsOf = (field) => filterVals[valKey(field)];
  // Has the Event picker settled? Until it has (the list is still loading, or the
  // default-pin hasn't run), an unscoped value fetch would return every event's
  // values — so the other dropdowns wait for it.
  const eventSettled = !eventField || !!eventValue || Array.isArray(filterVals[eventField]);
  // Lazily fetch the distinct values for a filter dimension (scoped to this event).
  const loadFilterVals = (field, opts = {}) => {
    if (!field || !curExplore) return;
    if (!opts.allEvents && !eventSettled) return; // the event lands first; the effect below re-runs
    const key = opts.allEvents ? field : valKey(field);
    const cur = filterVals[key];
    // Skip only in-flight or successfully loaded — an EMPTY result (a failed or
    // too-early fetch) stays retryable, otherwise the value dropdown degrades to
    // a free-text box forever and never recovers.
    if (cur === 'loading' || (Array.isArray(cur) && cur.length)) return;
    setFilterVals((m) => ({ ...m, [key]: 'loading' }));
    // allEvents: the Event picker lists EVERY event of this client (organiser scope),
    // not just the one this suite is pinned to — so you can pin the alert to any event.
    // Every OTHER dropdown rides the event already chosen: it narrows the values to that
    // event, and it's what lets them resolve with no dashboard tile on this explore.
    const scoped = (!opts.allEvents && eventField && eventValue) ? { [eventField]: eventValue } : null;
    api.alertMetricFilterValues(suiteId, { model: curExplore.model, view: curExplore.view, field, ...(opts.allEvents ? { allEvents: true } : {}), ...(scoped ? { filters: scoped } : {}) })
      .then((r) => setFilterVals((m) => ({ ...m, [key]: r.values || [] })))
      .catch(() => setFilterVals((m) => ({ ...m, [key]: [] })));
  };
  // Index-safe, and event-safe whatever index they are handed — see lib/metricFilters.
  const setFilter = (i, patch) => setMFilters((fs) => setFilterAt(fs, i, patch, eventField));
  const addFilter = () => setMFilters((fs) => [...fs, { field: '', value: '' }]);
  const removeFilter = (i) => setMFilters((fs) => removeFilterAt(fs, i, eventField));

  // Live value of the chosen target (tile OR built metric), so the threshold is set
  // against the real number.
  useEffect(() => {
    let alive = true;
    const done = (v) => { if (alive) setPreview(v); };
    if (source === 'tile') {
      if (!dashboardId || !tileId || !suiteId) { setPreview(null); return undefined; }
      setPreview({ loading: true });
      api.alertTileValue(suiteId, dashboardId, tileId).then((r) => done({ value: r.value })).catch(() => done({ value: null }));
    } else {
      if (!measure || !suiteId) { setPreview(null); return undefined; }
      // Opening a SAVED metric alert sets model/view/measure straight away, but the
      // explore catalogue lands a moment later — so hold on "reading…" until it does
      // (`exCat` is in the deps, so this re-runs then). Without that the box sat on
      // "couldn't read it right now" until you re-picked the measure by hand.
      if (!curExplore) { done(exCat ? { value: null } : { loading: true }); return undefined; }
      setPreview({ loading: true });
      api.alertMetricValue(suiteId, { model: curExplore.model, view: curExplore.view, measure, filters: metricFiltersObj() })
        .then((r) => done({ value: r.value })).catch(() => done({ value: null }));
    }
    return () => { alive = false; };
  }, [source, dashboardId, tileId, suiteId, exKey, measure, exCat, JSON.stringify(mFilters)]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reusable templates for this client (their own + global).
  useEffect(() => { if (entityId) api.alertTemplates(entityId).then((r) => setTemplates(r.templates || [])).catch(() => {}); }, [entityId]);
  // The client's own categories (shared with goals) — merged with the presets below.
  useEffect(() => { if (entityId) api.categories(entityId).then((r) => setCustomCats(r.categories || [])).catch(() => {}); }, [entityId]);
  const allTags = [...new Set([...ALERT_TAGS, ...customCats])];
  const tagIsNew = !!tag.trim() && !allTags.some((c) => c.toLowerCase() === tag.trim().toLowerCase());
  const saveCategory = async () => {
    const name = tag.trim(); if (!name || !entityId) return;
    try { const r = await api.addCategory(entityId, name); setCustomCats(r.categories || []); } catch { /* ignore */ }
  };

  // A template that names its metric by LABEL (no fixed model/view — e.g. GA4,
  // whose LookML names differ per instance): once the catalogue arrives, find the
  // explore whose label matches, then the measure whose label matches.
  useEffect(() => {
    if (source !== 'metric' || !pendingMetric || !exCat) return;
    const { exploreLabel, measureLabel } = pendingMetric;
    const ex = (exCat.explores || []).find((e) => new RegExp(exploreLabel, 'i').test(e.label || '') || new RegExp(exploreLabel, 'i').test(e.view || ''));
    if (ex) {
      setExKey(`${ex.model}::${ex.view}`);
      const m = (ex.measures || []).find((x) => new RegExp(measureLabel, 'i').test(x.label || '') || new RegExp(measureLabel, 'i').test(x.name || ''))
        || (ex.measures || [])[0];
      if (m) setMeasure(m.name);
    }
    setPendingMetric(null); // no match → the user picks; the rule shape is already set
  }, [source, pendingMetric, exCat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the event list for the picker, and default-pin to THIS event (the suite the
  // alert belongs to) when its name is among the values — so a new alert is correctly
  // scoped out of the box, while staying changeable.
  useEffect(() => { if (source === 'metric' && curExplore && eventField) loadFilterVals(eventField, { allEvents: true }); }, [source, exKey, eventField]); // eslint-disable-line react-hooks/exhaustive-deps
  // Filters that arrived via a template (field set, value blank) never went through
  // the field <select>'s onChange, so load their value dropdowns once the explore
  // resolves — loadFilterVals no-ops anything already loaded.
  // …and re-run when the EVENT changes (or first lands), so every value dropdown is
  // refetched under the event now in force instead of showing the previous event's.
  useEffect(() => { if (source === 'metric' && curExplore) for (const f of mFilters) if (f.field) loadFilterVals(f.field); }, [source, exKey, curExplore, mFilters.length, eventValue, eventSettled]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (source !== 'metric' || !eventField || eventValue) return;
    const vals = filterVals[eventField];
    if (!Array.isArray(vals) || !vals.length) return;
    const norm = (s) => String(s).replace(/\s+/g, ' ').trim().toLowerCase();
    // The event this suite is LOCKED to is the one Looker knows — pin that exactly.
    // Only when the suite has no event lock fall back to guessing from its display
    // name (a suite called "Liquideep" is, in Looker, "Liquideep - 5 September 2026").
    const match = (suiteEvent && vals.find((v) => norm(v) === norm(suiteEvent)))
      || (suiteName && vals.find((v) => norm(v) === norm(suiteName) || norm(v).includes(norm(suiteName)) || norm(suiteName).includes(norm(v))));
    if (match) setEventValue(match);
  }, [filterVals, eventField, eventValue, suiteEvent, suiteName, source]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve a template's tile ref against this client's catalogue (by id, else by name).
  useEffect(() => {
    if (!pendingRefs || !cat?.dashboards) return;
    const ds = cat.dashboards;
    const d = ds.find((x) => x.dashboardId === pendingRefs.dashboardId) || (pendingRefs.dashboardName && ds.find((x) => (x.title || '') === pendingRefs.dashboardName));
    if (d) {
      setDashboardId(d.dashboardId);
      const t = (d.tiles || []).find((x) => x.tileId === pendingRefs.tileId) || (pendingRefs.tileName && (d.tiles || []).find((x) => (x.title || '') === pendingRefs.tileName));
      setTileId(t ? t.tileId : '');
    }
    setPendingRefs(null);
  }, [pendingRefs, cat]);

  // Opened from a template (the page's Templates tab) — apply it once.
  const tmplApplied = useRef(false);
  useEffect(() => { if (initialTemplate && !tmplApplied.current) { tmplApplied.current = true; applySavedTemplate(initialTemplate); } }, [initialTemplate]);  

  const dashboards = cat?.dashboards || [];
  // An alert watches ONE headline number, so only single-value (KPI) tiles qualify
  // (mirrors the goal editor's test).
  const isKpi = (t) => { const v = t.visType || ''; return v === 'single_value' || v === 'single_value_period_over_period' || v.includes('bar_gauge'); };
  const tilesFor = (dId) => (dashboards.find((d) => d.dashboardId === dId)?.tiles || []).filter(isKpi);
  const tileName = () => { const d = dashboards.find((x) => x.dashboardId === dashboardId); return d?.tiles?.find((x) => x.tileId === tileId)?.title || ''; };
  const dashName = () => dashboards.find((x) => x.dashboardId === dashboardId)?.title || '';

  // Apply a SAVED template payload (the full reusable setup).
  const applySavedTemplate = (p) => {
    if (!p) return;
    if (p.name) setName(p.name);
    if (p.ruleType) setRuleType(p.ruleType);
    if (p.operator) setOperator(p.operator);
    if (p.unit) setUnit(p.unit);
    if (p.tag) setTag(p.tag);
    if (p.threshold != null) setThreshold(String(p.threshold));
    if (Array.isArray(p.channels)) setChannels(p.channels);
    if (p.priority) setPriority(p.priority);
    if (p.frequency) setFrequency(p.frequency);
    if (p.cooldownMin != null) setCooldownMin(String(p.cooldownMin));
    if (p.quietStart != null) setQuietStart(p.quietStart);
    if (p.quietEnd != null) setQuietEnd(p.quietEnd);
    setFilterAllow(Array.isArray(p.filterAllow) && p.filterAllow.length ? p.filterAllow : null);
    if (p.source === 'metric' && p.metricRef) {
      setSource('metric');
      if (p.metricRef.model && p.metricRef.view) {
        setExKey(`${p.metricRef.model}::${p.metricRef.view}`);
        setMeasure(p.metricRef.measure || '');
      } else if (p.metricRef.exploreLabel) {
        setExKey(''); setMeasure('');
        setPendingMetric({ exploreLabel: p.metricRef.exploreLabel, measureLabel: p.metricRef.measureLabel || '' }); // resolved by label once the catalogue loads
      }
      setMFilters(Object.entries(p.metricRef.metricFilters || {}).map(([field, value]) => ({ field, value })));
    } else if (p.source === 'tile') {
      setSource('tile');
      if (p.tileRef) setPendingRefs(p.tileRef); // resolved by id/name once the tile catalogue loads
    }
  };

  // The reusable subset of the current form (no live state / SMS numbers).
  const templatePayload = () => ({
    name, ruleType, source,
    tileRef: source === 'tile' && dashboardId && tileId ? { dashboardId, tileId, dashboardName: dashName(), tileName: tileName() } : null,
    metricRef: source === 'metric' && curExplore && measure ? { model: curExplore.model, view: curExplore.view, measure, measureLabel: measureObj()?.label || '', metricFilters: metricFiltersObj(), metricLabel: metricLabelStr() } : null,
    operator: ruleType === 'depletion' || ruleType === 'sold_out' ? 'lte' : operator,
    threshold: ruleType === 'sold_out' ? 0 : (threshold === '' ? 0 : Number(threshold)),
    unit, tag, channels, priority, frequency, cooldownMin: Number(cooldownMin) || 60, quietStart, quietEnd,
  });

  const saveAsTemplate = async () => {
    const nm = (name || '').trim() || (window.prompt('Template name?') || '');
    if (!nm.trim() || !entityId) return;
    if (source === 'metric' ? !(curExplore && measure) : !(dashboardId && tileId)) { setErr('Pick what to watch before saving a template.'); return; }
    // Admins can publish a portable scaffold to every client.
    const global = isAdmin && window.confirm('Make this available to ALL clients?\n\nOK = global template (every client)\nCancel = just this client');
    setTmplBusy(true);
    try {
      const r = await api.saveAlertTemplate({ entityId, name: nm.trim(), payload: { ...templatePayload(), name: nm.trim() }, global });
      if (r?.template) { setTemplates((t) => [r.template, ...t.filter((x) => x.id !== r.template.id)]); window.alert(`Saved “${r.template.name}”${r.template.global ? ' as a GLOBAL template (all clients)' : ''}.`); }
    } catch (e) { window.alert(`Couldn't save template: ${e.message}`); }
    finally { setTmplBusy(false); }
  };
  const removeTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.deleteAlertTemplate(id); setTemplates((t) => t.filter((x) => x.id !== id)); } catch { /* ignore */ }
  };

  const toggleChannel = (c) => setChannels((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  // A "% left on any ticket type/category" alert (made from the Sales Command Centre):
  // its type/category list is fixed; here you change the % and the delivery.
  const stockNoun = /categor/.test(alert?.groupBy || '') ? 'ticket category' : 'ticket type';
  // The plain-English read-back of the rule (always visible, so it's never a mystery).
  const sentence = () => {
    const metric = (source === 'metric' ? metricLabelStr() : tileName()) || 'the number';
    const tval = threshold === '' ? '…' : fmtNum(Number(threshold), unit);
    let when;
    if (ruleType === 'stock_pct') when = `any ${stockNoun} has ${threshold === '' ? '…' : threshold}% or fewer left`;
    else if (ruleType === 'sold_out') when = `${metric} sells out`;
    else if (ruleType === 'depletion') when = `${metric} drops below ${tval}`;
    else when = `${metric} ${operator === 'lte' || operator === 'lt' ? 'drops to' : 'reaches'} ${tval}`;
    const via = ['in your inbox', ...channels.map((c) => ({ push: 'push', email: 'email', sms: 'SMS', slack: 'Slack' }[c]))].filter(Boolean);
    const viaTxt = via.length > 1 ? `${via.slice(0, -1).join(', ')} and ${via[via.length - 1]}` : via[0];
    const freq = frequency === 'once' ? 'once' : `repeatedly, at most ${cadenceText(cooldownMin)}`;
    return `When ${when}, notify me via ${viaTxt} — ${freq}.`;
  };

  async function save() {
    if (!name.trim()) { setErr('Give the alert a name.'); return; }
    if (source === 'metric') { if (!curExplore || !measure) { setErr('Pick the metric to watch.'); return; } }
    else if (!dashboardId || !tileId) { setErr('Pick the dashboard tile to watch.'); return; }
    if (ruleType !== 'sold_out' && (threshold === '' || Number.isNaN(Number(threshold)))) { setErr('Set a number to alert on.'); return; }
    if (!channels.length) { setErr('Pick at least one way to be notified (or just the inbox stays on).'); }
    setBusy(true); setErr('');
    const body = {
      name: name.trim(), ruleType, source,
      operator: ruleType === 'depletion' || ruleType === 'sold_out' || ruleType === 'stock_pct' ? 'lte' : operator,
      threshold: ruleType === 'sold_out' ? 0 : Number(threshold),
      unit, tag: tag.trim(), channels,
      smsRecipients: channels.includes('sms') ? smsRecipients.split(',').map((s) => s.trim()).filter(Boolean) : [],
      recipients,
      priority, frequency,
      cooldownMin: Number(cooldownMin) || 60,
      quietStart, quietEnd,
    };
    if (source === 'metric') {
      body.model = curExplore.model; body.view = curExplore.view; body.measure = measure;
      body.measureLabel = measureObj()?.label || ''; body.metricFilters = metricFiltersObj(); body.metricLabel = metricLabelStr();
    } else {
      body.dashboardId = dashboardId; body.tileId = tileId; body.dashboardName = dashName(); body.tileName = tileName();
    }
    try {
      if (editing) await api.updateAlert(alert.id, body);
      else await api.createAlert(suiteId, body);
      onSaved?.(); onClose();
    } catch (e) { setErr(e.message || 'Could not save the alert.'); setBusy(false); }
  }

  async function del() {
    setBusy(true); setErr('');
    try { await api.deleteAlert(alert.id); onSaved?.(); onClose(); }
    catch (e) { setErr(e.message || 'Could not delete the alert.'); setBusy(false); }
  }

  async function test() {
    if (!editing) { setErr('Save the alert first, then send a test.'); return; }
    setTesting(true); setErr('');
    try {
      const r = await api.testAlert(alert.id);
      const smsFail = (r.smsFailed || []).map((f) => `• ${f.to}: ${f.error}`).join('\n');
      window.alert(`Test sent.\n\n“${r.message}”\n\nDelivered via: ${(r.channels || []).join(', ') || 'inbox'}${smsFail ? `\n\nSMS didn't go through to:\n${smsFail}` : ''}`);
    } catch (e) { setErr(e.message || 'Could not send a test.'); }
    finally { setTesting(false); }
  }

  return (
    <div style={overlay(isMobile)} onClick={onClose}>
      <div style={{ ...sheet(isMobile), maxWidth: isMobile ? '100%' : 460 }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 20 }}>🔔</span>
          <h2 style={{ fontSize: 17, fontWeight: 800, flex: 1 }}>{editing ? 'Edit alert' : 'New alert'}{suiteName ? <span style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 13 }}> · {suiteName}</span> : null}</h2>
          <button onClick={onClose} style={xBtn} aria-label="Close">✕</button>
        </div>

        {/* Template gallery — ONE list, the same saved templates the Templates tab
            manages: Howler's starter pack first, then this client's own. Tap a card,
            tweak one number. (The old hardcoded cards lived only in this file and
            drifted from the pack — now the pack IS the gallery.) */}
        {!editing && templates.length > 0 && (
          <Field label="Start from">
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
              {[...templates].sort((a, b) => (String(b.id).startsWith('seed-') ? 1 : 0) - (String(a.id).startsWith('seed-') ? 1 : 0)).map((t) => (
                <div key={t.id} role="button" tabIndex={0} onClick={() => applySavedTemplate(t.payload)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') applySavedTemplate(t.payload); }}
                  style={{ ...tmplCard(false), position: 'relative', cursor: 'pointer' }}
                  title={t.payload?.meta?.blurb || (t.global ? 'Global template (links your own data)' : 'Use this template')}>
                  <span style={{ fontSize: 18 }}>{t.payload?.meta?.emoji || (t.global ? '🌐' : '📑')}</span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{t.payload?.name || t.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--muted)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {t.payload?.meta?.blurb || t.payload?.metricRef?.metricLabel || t.payload?.tileRef?.tileName || 'Saved template'}
                  </span>
                  {(!t.global || isAdmin) && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); removeTemplate(t.id); }} aria-label="Delete template"
                      style={{ position: 'absolute', top: 6, right: 6, minWidth: 28, minHeight: 28, border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 12 }}>✕</button>
                  )}
                </div>
              ))}
            </div>
          </Field>
        )}

        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. VIP nearly gone, R1m revenue" style={inp} autoFocus />
        </Field>

        <Field label="Category (optional)" hint="Group this alert by operational area — the Alerts page shows one row per category. Pick one or create your own (shared with goals).">
          <input value={tag} onChange={(e) => setTag(e.target.value)} list="alert-tag-presets" placeholder="e.g. Ticketing, Cashless, Access control" style={inp} />
          <datalist id="alert-tag-presets">
            {allTags.map((t) => <option key={t} value={t} />)}
          </datalist>
          {tagIsNew && (
            <button type="button" onClick={saveCategory} style={addCatBtn}>＋ Save “{tag.trim()}” as a category</button>
          )}
        </Field>

        <Field label="What should we watch?">
          <div style={{ display: 'flex', gap: 8 }}>
            <Seg active={source === 'tile'} onClick={() => setSource('tile')}>📊 A dashboard tile</Seg>
            <Seg active={source === 'metric'} onClick={() => setSource('metric')}>🧩 Build a metric</Seg>
          </div>
        </Field>

        {source === 'tile' ? (
          <Field label="Which tile?" hint="Pick a single-value (KPI) tile you already look at — the alert watches that live number.">
            <DashboardPicker value={dashboardId} onChange={(v) => { setDashboardId(v); setTileId(''); }} dashboards={dashboards} isMobile={isMobile} preferSuiteId={suiteId} placeholder={cat ? 'Choose a dashboard…' : 'Loading…'} />
            {dashboardId && (tilesFor(dashboardId).length ? (
              <select value={tileId} onChange={(e) => setTileId(e.target.value)} style={{ ...inp, marginTop: 8 }}>
                <option value="">Choose a tile…</option>
                {tilesFor(dashboardId).map((t) => <option key={t.tileId} value={t.tileId}>{t.title}</option>)}
              </select>
            ) : (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
                No single-value (KPI) tiles on this dashboard. Alerts watch one headline number — pick a dashboard with a KPI tile, or “Build a metric”.
              </div>
            ))}
          </Field>
        ) : (
          <Field label="Build the metric" hint="Choose the data, filter it to the slice you care about (e.g. Ticket Type = VIP), then pick the measure. No tile needed.">
            {/* 1 — the data source */}
            {/* Describe it instead of hunting for the field — fills the pickers below. */}
            <AskOwlMetric suiteId={suiteId} entityId={entityId} inputStyle={inp} onFilled={fillFromOwl} />
            <select value={exKey} onChange={(e) => { setExKey(e.target.value); setMeasure(''); setMFilters([]); }} style={inp}>
              <option value="">{exCat ? 'Choose a data source…' : 'Loading…'}</option>
              {explores.map((e) => <option key={`${e.model}::${e.view}`} value={`${e.model}::${e.view}`}>{e.label}</option>)}
            </select>
            {exCat && !explores.length && (
              <div style={{ marginTop: 8, fontSize: 12, color: (exCat.error || (exCat.skipped || []).length) ? '#b45309' : 'var(--muted)', lineHeight: 1.4 }}>
                {exCat.error
                  ? `Couldn't load the data sources for this event: ${exCat.error}. Use “A dashboard tile” for now — and send this message to Howler.`
                  : (exCat.skipped || []).length
                    ? `Couldn't read ${exCat.skipped.length} data source${exCat.skipped.length === 1 ? '' : 's'} from Looker right now (${exCat.skipped[0].error}). Try again in a minute, or use “A dashboard tile” — and tell Howler if this keeps happening.`
                    : exCat.dashboardCount === 0
                      ? 'No dashboards are linked to this event yet, so there’s nothing to build a metric from. Use “A dashboard tile” instead.'
                      : exCat.tileExplores === 0
                        ? `This event's ${exCat.dashboardCount} dashboard${exCat.dashboardCount === 1 ? '' : 's'} have no queryable tiles — use “A dashboard tile”, and tell Howler this event needs its dashboards wired up.`
                        : exCat.noMeasures
                          ? `Found ${exCat.noMeasures} data source${exCat.noMeasures === 1 ? '' : 's'}, but Looker exposes no measure on ${exCat.noMeasures === 1 ? 'it' : 'them'} — tell Howler; use “A dashboard tile” meanwhile.`
                          : 'No data sources available yet (these come from your existing dashboards). Use “A dashboard tile” instead.'}
              </div>
            )}
            {/* Event — pin the alert to ONE event so the number can never span others. */}
            {curExplore && eventField && (
              <div style={{ marginTop: 10 }}>
                <div style={subLabel}>Event</div>
                {Array.isArray(valsOf(eventField)) ? (
                  <select value={eventValue} onChange={(e) => setEventValue(e.target.value)} style={inp}>
                    <option value="">All events in scope</option>
                    {valsOf(eventField).map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                ) : <div style={{ fontSize: 12, color: 'var(--muted)' }}>Loading events…</div>}
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Pins this alert to one event, so its number can’t include other events. Defaults to {suiteName ? <b>{suiteName}</b> : 'this event'}.</div>
              </div>
            )}
            {/* 2 — the filter(s) (Ticket Type / Category …) */}
            {curExplore && (
              <div style={{ marginTop: 10 }}>
                <div style={subLabel}>Filter (optional)</div>
                {/* The event has its own picker above, so it is hidden here — and each row
                    carries its REAL index (`f.i`), never its position on screen. Using
                    the on-screen position is what made choosing a Ticket Type overwrite
                    the event (see lib/metricFilters). */}
                {visibleFilters(mFilters, eventField).map((f) => {
                  const vals = valsOf(f.field);
                  const ready = Array.isArray(vals) && vals.length;
                  // A value carried over from another event (or a template) may not exist
                  // on the event now chosen — keep it selectable so nothing silently
                  // changes, but say so: that slice would read 0.
                  const orphan = ready && f.value && !vals.includes(f.value);
                  return (
                  <div key={f.i} style={{ marginBottom: 6 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                    <select value={f.field} onChange={(e) => { setFilter(f.i, { field: e.target.value, value: '' }); loadFilterVals(e.target.value); }} style={{ ...inp, flex: 1 }}>
                      <option value="">Filter by…</option>
                      {dimsFor().filter((d) => d.name !== eventField).map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
                    </select>
                    {f.field && (
                      ready
                        ? <select value={f.value} onChange={(e) => setFilter(f.i, { value: e.target.value })} style={{ ...inp, flex: 1 }}>
                            <option value="">Choose a value…</option>
                            {orphan && <option value={f.value}>{f.value} (not on this event)</option>}
                            {vals.map((v) => <option key={v} value={v}>{v}</option>)}
                          </select>
                        : <input value={f.value} onChange={(e) => setFilter(f.i, { value: e.target.value })} onFocus={() => loadFilterVals(f.field)} placeholder={vals === 'loading' ? 'loading values…' : 'value (e.g. VIP)'} style={{ ...inp, flex: 1 }} />
                    )}
                    <button type="button" onClick={() => removeFilter(f.i)} aria-label="Remove filter" style={msX}>✕</button>
                    </div>
                    {orphan && (
                      <div style={{ fontSize: 11, color: '#b45309', marginTop: 3, lineHeight: 1.4 }}>
                        “{f.value}” isn’t a value on {eventValue || 'this event'} — pick one from the list, or this alert reads 0.
                      </div>
                    )}
                  </div>
                  );
                })}
                <button type="button" onClick={addFilter} style={addFilterBtn}>＋ Add a filter (e.g. Ticket Type, Category)</button>
              </div>
            )}
            {/* 3 — the measure */}
            {curExplore && (
              <div style={{ marginTop: 10 }}>
                <div style={subLabel}>Measure</div>
                <select value={measure} onChange={(e) => { setMeasure(e.target.value); const m = measuresFor().find((x) => x.name === e.target.value); if (m && /revenue|amount|sales|gross|net|value|spend/i.test(`${m.label} ${m.name}`)) setUnit('ZAR'); }} style={inp}>
                  <option value="">Choose a measure…</option>
                  {measuresFor().map((m) => <option key={m.name} value={m.name}>{m.label}</option>)}
                </select>
              </div>
            )}
          </Field>
        )}

        {/* Shared live-value box — what the chosen target reads right now. */}
        {((source === 'tile' && tileId) || (source === 'metric' && measure)) && (
          <div style={readsBox}>
            <span style={{ color: 'var(--muted)', fontWeight: 600 }}>This reads:</span>
            {preview?.loading ? <span style={{ color: 'var(--muted)' }}>reading…</span>
              : preview && preview.value != null ? <b style={{ fontSize: 15 }}>{fmtNum(preview.value, unit)}</b>
                : <span style={{ color: 'var(--muted)' }}>— couldn't read it right now</span>}
            <span style={{ flex: 1 }} />
            {preview && preview.value != null && threshold === '' && ruleType !== 'sold_out' && (
              <button type="button" onClick={() => setThreshold(String(Math.round(Number(preview.value))))} style={miniBtn}>Use as start</button>
            )}
          </div>
        )}
        {((source === 'tile' && tileId) || (source === 'metric' && measure)) && (
          <div style={{ fontSize: 11.5, color: 'var(--muted)', margin: '5px 2px 0', lineHeight: 1.4 }}>
            📍 Scoped to <b style={{ color: 'var(--text)' }}>{scopeLabel()}</b> — plus this client's own data only.
          </div>
        )}

        <Field label="Alert me when…">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {alert?.ruleType === 'stock_pct' && <Seg active={ruleType === 'stock_pct'} onClick={() => setRuleType('stock_pct')}>% left on any {stockNoun}</Seg>}
            <Seg active={ruleType === 'threshold'} onClick={() => setRuleType('threshold')}>📈 Crosses a number</Seg>
            <Seg active={ruleType === 'depletion'} onClick={() => setRuleType('depletion')}>⚠️ Drops below</Seg>
            <Seg active={ruleType === 'sold_out'} onClick={() => { setRuleType('sold_out'); setThreshold('0'); }}>🎉 Sold out</Seg>
          </div>
        </Field>

        {ruleType === 'threshold' && (
          <div style={{ display: 'flex', gap: 10 }}>
            <Field label="Direction" style={{ flex: 1 }}>
              <select value={operator} onChange={(e) => setOperator(e.target.value)} style={inp}>
                <option value="gte">Rises to / above ↑</option>
                <option value="lte">Drops to / below ↓</option>
              </select>
            </Field>
            <Field label="Number" style={{ flex: 1 }}>
              <input value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="e.g. 5000" inputMode="decimal" style={inp} />
            </Field>
            <Field label="Unit" style={{ width: 96 }}>
              <UnitSelect unit={unit} setUnit={setUnit} />
            </Field>
          </div>
        )}
        {ruleType === 'depletion' && (
          <div style={{ display: 'flex', gap: 10 }}>
            <Field label="When fewer than" style={{ flex: 1 }}>
              <input value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="e.g. 100" inputMode="decimal" style={inp} />
            </Field>
            <Field label="Unit" style={{ width: 96 }}>
              <UnitSelect unit={unit} setUnit={setUnit} />
            </Field>
          </div>
        )}
        {ruleType === 'stock_pct' && (
          <Field label={`When any ${stockNoun} has this % or fewer left`} hint={`Watches every ${stockNoun} of the event, including ones added later, and tells you about each one as it runs low.`}>
            <input value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="e.g. 10" inputMode="decimal" style={{ ...inp, maxWidth: 140 }} />
          </Field>
        )}
        {ruleType === 'sold_out' && (
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.45 }}>
            Fires the moment this number hits zero — the big one. No threshold to set.
          </div>
        )}

        <Field label="How should we tell you?" hint="It always lands in your Pulse inbox. Add any of these on top.">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Chip active={channels.includes('push')} onClick={() => toggleChannel('push')}>📱 Push</Chip>
            <Chip active={channels.includes('email')} onClick={() => toggleChannel('email')}>✉️ Email</Chip>
            <Chip active={channels.includes('sms')} onClick={() => toggleChannel('sms')} disabled={!smsAvailable} title={smsAvailable ? '' : 'SMS isn’t configured for this client yet'}>💬 SMS</Chip>
            <Chip active={channels.includes('slack')} onClick={() => toggleChannel('slack')} disabled={!slackAvailable} title={slackAvailable ? '' : 'Slack isn’t connected for this client yet (Settings → Integrations)'}># Slack</Chip>
          </div>
          {channels.includes('sms') && (
            <input value={smsRecipients} onChange={(e) => setSmsRecipients(e.target.value)} placeholder="Mobile numbers, comma-separated (e.g. 0821234567)" style={{ ...inp, marginTop: 8 }} />
          )}
          {channels.includes('sms') && (
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>Numbers outside South Africa need their country code, starting with + or 00 (e.g. +44 7911 123456). Use Test to check each number gets through.</div>
          )}
        </Field>

        {/* Who it goes to. Not everything needs the whole team — a bar-revenue
            alert is the ops lead's business, not marketing's. Naming people makes
            the alert theirs: they alone are notified AND it's only in their inbox. */}
        <Field label="Who should get it?" hint={recipients.length ? 'Only these people are notified, and only they see it in the inbox.' : 'Everyone on this client\u2019s team, by their own notification settings.'}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Chip active={!recipients.length} onClick={() => setRecipients([])}>Everyone</Chip>
            {(team || []).map((m) => (
              <Chip
                key={m.id}
                active={recipients.includes(m.id)}
                onClick={() => setRecipients((r) => (r.includes(m.id) ? r.filter((x) => x !== m.id) : [...r, m.id]))}
                              >{m.name}</Chip>
            ))}
          </div>
          {team === null && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>Loading the team…</div>}
          {team !== null && !team.length && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>No other logins on this client yet — it goes to the whole team.</div>}
        </Field>

        <div style={{ display: 'flex', gap: 10 }}>
          <Field label="Importance" hint="Important alerts ignore quiet hours." style={{ flex: 1 }}>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} style={inp}>
              <option value="normal">Normal</option>
              <option value="important">Important (always reach me)</option>
            </select>
          </Field>
          <Field label="How often" style={{ flex: 1 }}>
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)} style={inp}>
              <option value="once">Once, then rest</option>
              <option value="repeat">Every time it crosses</option>
            </select>
          </Field>
        </div>

        {frequency === 'repeat' && (
          <Field label="Remind me at most" hint="A condition that stays true (sessions below target all week) would otherwise alert on every check. This is the least time between repeats.">
            <select value={cadenceKey} onChange={(e) => { const k = e.target.value; if (k !== 'custom') setCooldownMin(String(CADENCES[k])); else setCooldownMin(String(Number(cooldownMin) || 1440)); }} style={inp}>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly (30 days)</option>
              <option value="custom">Custom — every X days</option>
            </select>
            {cadenceKey === 'custom' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Every</span>
                <input value={customDays} onChange={(e) => { const d = Math.max(1, Math.min(365, Math.round(Number(e.target.value) || 0))); setCooldownMin(String(d * 1440)); }}
                  inputMode="numeric" aria-label="Days between reminders" style={{ ...inp, width: 90 }} />
                <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>day{customDays === 1 ? '' : 's'}</span>
              </div>
            )}
          </Field>
        )}

        <Field label="Quiet hours (optional)" hint="Hold non-important alerts during these hours (your timezone). Leave blank for none.">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input type="time" value={quietStart} onChange={(e) => setQuietStart(e.target.value)} style={{ ...inp, flex: 1 }} />
            <span style={{ color: 'var(--muted)', fontSize: 12 }}>to</span>
            <input type="time" value={quietEnd} onChange={(e) => setQuietEnd(e.target.value)} style={{ ...inp, flex: 1 }} />
          </div>
        </Field>

        {/* Plain-English read-back — the rule, in a sentence. */}
        <div style={sentenceBox}>{sentence()}</div>

        {err && <div style={{ color: 'var(--error, #dc2626)', fontSize: 12.5, marginTop: 10 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center' }}>
          {editing && (confirmDel ? (
            <button onClick={del} disabled={busy} style={btnDanger}>Delete alert</button>
          ) : (
            <button onClick={() => setConfirmDel(true)} style={btnDelGhost} aria-label="Delete alert" title="Delete this alert">🗑</button>
          ))}
          {editing && <button onClick={test} disabled={testing} style={btnGhost} title="Send a test notification now">{testing ? 'Sending…' : '🔔 Test'}</button>}
          {name.trim() && <button onClick={saveAsTemplate} disabled={tmplBusy} style={btnGhost} title="Save this setup as a reusable template">{tmplBusy ? 'Saving…' : '📑 Template'}</button>}
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={save} disabled={busy} style={btnPrimary}>{busy ? 'Saving…' : (editing ? 'Save alert' : 'Create alert')}</button>
        </div>
      </div>
    </div>
  );
}

function UnitSelect({ unit, setUnit }) {
  return (
    <select value={unit} onChange={(e) => setUnit(e.target.value)} style={inp}>
      {[...new Set(['tickets', 'ZAR', '%', 'orders', 'count', unit].filter(Boolean))].map((u) => <option key={u} value={u}>{u}</option>)}
    </select>
  );
}

function fmtNum(v, unit) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const s = Math.abs(n) >= 1000 ? Math.round(n).toLocaleString('en-ZA') : String(n);
  if (unit === 'ZAR') return `R${s}`;
  if (unit === '%') return `${s}%`;
  return unit && unit !== 'count' ? `${s} ${unit}` : s;
}

function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 5 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.4 }}>{hint}</div>}
    </div>
  );
}
function Seg({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      flex: '1 1 auto', padding: '9px 8px', borderRadius: 10, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
      border: `1.5px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`,
      background: active ? 'rgba(var(--brand-rgb,10,132,255),0.10)' : 'var(--card)',
      color: active ? 'var(--brand)' : 'var(--text)',
    }}>{children}</button>
  );
}
function Chip({ active, onClick, disabled, title, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} style={{
      padding: '8px 13px', borderRadius: 980, fontSize: 12.5, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
      border: `1.5px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`, opacity: disabled ? 0.45 : 1,
      background: active ? 'rgba(var(--brand-rgb,10,132,255),0.10)' : 'var(--card)',
      color: active ? 'var(--brand)' : 'var(--text)',
    }}>{children}</button>
  );
}
const tmplCard = (active) => ({
  display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start', textAlign: 'left',
  padding: '10px 12px', borderRadius: 12, cursor: 'pointer',
  border: `1.5px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`,
  background: active ? 'rgba(var(--brand-rgb,10,132,255),0.07)' : 'var(--card)', color: 'var(--text)',
});

// Mobile = a FULL-SCREEN sheet (no floating dialog to fight with): the sheet is
// the only scroll container, sized with dvh so iOS Safari's collapsing URL bar
// can't hide the save row, and overscroll is contained so hitting the top/bottom
// never hands the swipe to the page behind.
const overlay = (isMobile) => ({
  position: 'fixed', inset: 0, background: isMobile ? 'var(--bg, #fff)' : 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center',
  padding: isMobile ? 0 : 16, zIndex: 100, overscrollBehavior: 'contain',
});
const sheet = (isMobile) => ({
  width: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
  background: 'var(--card)', color: 'var(--text)',
  ...(isMobile
    ? { height: '100dvh', maxHeight: '100dvh', borderRadius: 0, border: 'none', padding: '18px 16px calc(24px + env(safe-area-inset-bottom))' }
    : { maxHeight: 'min(90vh, calc(100dvh - 32px))', borderRadius: 16, border: '1px solid var(--border)', padding: '18px 18px 20px', boxShadow: 'var(--shadow-lg, 0 12px 40px rgba(0,0,0,0.28))' }),
});
const inp = { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1.5px solid var(--hairline)', borderRadius: 9, fontSize: 14, outline: 'none', background: 'var(--card)', color: 'var(--text)', fontFamily: 'inherit' };
const xBtn = { border: 'none', background: 'rgba(128,128,128,0.12)', color: 'var(--muted-2)', borderRadius: 980, width: 28, height: 28, fontSize: 13, cursor: 'pointer' };
const readsBox = { marginTop: 9, padding: '8px 11px', background: 'rgba(128,128,128,0.07)', borderRadius: 9, fontSize: 13, display: 'flex', alignItems: 'center', gap: 7 };
const msX = { border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--muted)', borderRadius: 9, fontSize: 13, cursor: 'pointer', flexShrink: 0, width: 38 };
const addFilterBtn = { border: '1px dashed var(--hairline)', background: 'transparent', color: 'var(--brand)', borderRadius: 9, fontSize: 12, fontWeight: 700, padding: '7px 11px', cursor: 'pointer', width: '100%' };
const addCatBtn = { marginTop: 7, border: '1px dashed var(--hairline)', background: 'transparent', color: 'var(--brand)', borderRadius: 9, fontSize: 12, fontWeight: 700, padding: '6px 11px', cursor: 'pointer' };
const subLabel = { fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 5 };
const miniBtn = { border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--brand)', borderRadius: 980, fontSize: 11, fontWeight: 700, padding: '3px 9px', cursor: 'pointer' };
const sentenceBox = { marginTop: 6, padding: '11px 13px', background: 'rgba(var(--brand-rgb,10,132,255),0.07)', border: '1px solid var(--hairline)', borderRadius: 10, fontSize: 13, lineHeight: 1.5, fontWeight: 600 };
const btnGhost = { flex: '0 0 auto', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' };
const btnDanger = { flex: '0 0 auto', padding: '10px 14px', borderRadius: 10, border: 'none', background: 'var(--error, #dc2626)', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' };
const btnDelGhost = { flex: '0 0 auto', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--error, #dc2626)', fontSize: 15, cursor: 'pointer' };
const btnPrimary = { flex: 1, padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' };
